import React from 'react';
import { useNavigate } from 'react-router-dom';
import { Sparkles, Zap, ArrowRight } from 'lucide-react';
import { Button } from './ui/button';
import { useFreemium } from '../context/FreemiumContext';

/**
 * Muestra el saldo de créditos IA gratuitos del usuario. Oculto para admins,
 * usuarios con `unlimited_access` o con plan activo.
 */
export default function CreditosGratuitosCard() {
    const navigate = useNavigate();
    const {
        status, planActivo, unlimitedAccess, isAdmin,
        freeIaCredits, freeIaCreditsMax,
    } = useFreemium();

    // Ocultar para casos exentos, sin estado, o durante ventana de contingencia
    if (!status) return null;
    if (planActivo || unlimitedAccess || isAdmin) return null;
    if (status.global_access_override_active) return null;
    if (status.user_access_grant_active) return null;
    if (freeIaCredits === null || freeIaCredits === undefined) return null;

    const max = freeIaCreditsMax || 6;
    const usados = Math.max(0, max - freeIaCredits);
    const porcentaje = Math.min(100, Math.round((freeIaCredits / max) * 100));
    const sinSaldo = freeIaCredits <= 0;

    const barColor = sinSaldo
        ? 'bg-rose-500'
        : freeIaCredits <= 1
            ? 'bg-amber-500'
            : 'bg-emerald-500';

    return (
        <div
            data-testid="creditos-gratuitos-card"
            className="rounded-xl border border-slate-200 bg-white p-4 sm:p-5 shadow-sm"
        >
            <div className="flex items-start justify-between gap-3 flex-wrap">
                <div className="flex items-center gap-2 min-w-0">
                    <div className="grid place-items-center h-9 w-9 rounded-lg bg-indigo-50 text-indigo-600 shrink-0">
                        <Sparkles className="h-4 w-4" />
                    </div>
                    <div className="min-w-0">
                        <div className="text-[10px] font-bold uppercase tracking-widest text-slate-500">
                            Créditos IA gratuitos
                        </div>
                        <div className="font-heading text-base sm:text-lg font-semibold text-slate-900">
                            {sinSaldo ? 'Sin créditos disponibles' : `${freeIaCredits} de ${max} disponibles`}
                        </div>
                    </div>
                </div>
                <Button
                    size="sm"
                    onClick={() => navigate('/activar-plan')}
                    className={sinSaldo ? 'bg-rose-600 hover:bg-rose-700 gap-1.5' : 'bg-slate-900 hover:bg-slate-800 gap-1.5'}
                    data-testid="creditos-cta-planes"
                >
                    <Zap className="h-3.5 w-3.5" />
                    {sinSaldo ? 'Activar plan' : 'Ver planes'}
                    <ArrowRight className="h-3 w-3" />
                </Button>
            </div>

            {/* Barra de progreso */}
            <div className="mt-3">
                <div className="h-2 w-full rounded-full bg-slate-100 overflow-hidden">
                    <div
                        className={`h-full ${barColor} transition-all`}
                        style={{ width: `${porcentaje}%` }}
                        data-testid="creditos-progress-bar"
                    />
                </div>
                <div className="mt-1.5 flex items-center justify-between text-[11px] text-slate-500">
                    <span data-testid="creditos-usados">{usados} usado{usados === 1 ? '' : 's'}</span>
                    <span data-testid="creditos-restantes">
                        {sinSaldo ? 'Sin créditos' : `${freeIaCredits} restante${freeIaCredits === 1 ? '' : 's'}`}
                    </span>
                </div>
            </div>

            <p className="mt-3 text-xs text-slate-600 leading-relaxed">
                Cada crédito equivale a <strong>un análisis IA</strong> de un borrador. Al agotarlos,
                puedes activar un plan y seguir mejorando tu Portafolio Docente 2026.
            </p>
        </div>
    );
}
